import type { Position, RadarEntry, RadarConfig } from '@/types/radar';

/**
 * Simple seeded random number generator
 */
class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed % 2147483647;
    if (this.seed <= 0) this.seed += 2147483646;
  }

  next(): number {
    this.seed = (this.seed * 16807) % 2147483647;
    return (this.seed - 1) / 2147483646;
  }
}

/**
 * Convert string to number hash
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Calculates positions for radar blips with collision detection
 */
export class PositionCalculator {
  private positions: Map<string, Position> = new Map();
  private readonly minDistance = 30;
  private readonly maxAttempts = 100;
  private config: RadarConfig;
  private radius: number;

  constructor(config: RadarConfig, radius: number) {
    this.config = config;
    this.radius = radius;
  }

  /**
   * Calculate positions for all entries
   */
  calculate(entries: RadarEntry[]): Map<string, Position> {
    this.positions.clear();

    // Sort entries to place featured ones first
    const sortedEntries = [...entries].sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return 0;
    });

    for (const entry of sortedEntries) {
      const position = this.findNonCollidingPosition(entry);
      this.positions.set(entry.id, position);
    }

    return this.positions;
  }

  /**
   * Find a non-colliding position for an entry
   */
  private findNonCollidingPosition(entry: RadarEntry): Position {
    const quadrant = this.config.quadrants.find((q) => q.id === entry.quadrant);
    const ring = this.config.rings.find((r) => r.id === entry.ring);

    if (!quadrant || !ring) {
      throw new Error(`Invalid quadrant or ring for entry ${entry.id}`);
    }

    const ringCount = this.config.rings.length;
    const innerRadius = (ring.order * this.radius) / ringCount;
    const outerRadius = ((ring.order + 1) * this.radius) / ringCount;

    // Add padding to keep blips away from ring edges
    const padding = this.radius * 0.05;
    const adjustedInnerRadius = Math.max(0, innerRadius + padding);
    const adjustedOuterRadius = Math.max(adjustedInnerRadius, outerRadius - padding);

    // Calculate quadrant angle range
    const quadrantAngle = quadrant.order * 90;
    const minAngle = quadrantAngle - 40; // Slightly less than 45 to keep away from edges
    const maxAngle = quadrantAngle + 40;

    // Use seeded random based on entry ID for deterministic positions
    const seed = hashString(entry.id);
    const rng = new SeededRandom(seed);

    for (let attempt = 0; attempt < this.maxAttempts; attempt++) {
      const angle = minAngle + rng.next() * (maxAngle - minAngle);
      const r =
        adjustedInnerRadius + rng.next() * (adjustedOuterRadius - adjustedInnerRadius);

      const angleRad = (angle * Math.PI) / 180;
      const x = r * Math.cos(angleRad);
      const y = r * Math.sin(angleRad);

      if (!this.hasCollision(x, y)) {
        return { x, y, angle, radius: r };
      }
    }

    // Fallback: use center of quadrant/ring if no position found
    const angle = (minAngle + maxAngle) / 2;
    const r = (adjustedInnerRadius + adjustedOuterRadius) / 2;
    const angleRad = (angle * Math.PI) / 180;

    return {
      x: r * Math.cos(angleRad),
      y: r * Math.sin(angleRad),
      angle,
      radius: r,
    };
  }

  /**
   * Check if a position collides with existing blips
   */
  private hasCollision(x: number, y: number): boolean {
    for (const pos of this.positions.values()) {
      const distance = Math.sqrt(Math.pow(x - pos.x, 2) + Math.pow(y - pos.y, 2));
      if (distance < this.minDistance) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get position for a specific entry
   */
  getPosition(entryId: string): Position | undefined {
    return this.positions.get(entryId);
  }
}

/**
 * Generate SVG path for a star shape
 */
export function starPath(size: number): string {
  const points = 5;
  const outerRadius = size;
  const innerRadius = size / 2.5;
  let path = '';

  for (let i = 0; i < points * 2; i++) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const angle = (i * Math.PI) / points - Math.PI / 2;
    const x = radius * Math.cos(angle);
    const y = radius * Math.sin(angle);
    path += `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)},${y.toFixed(2)} `;
  }
  path += 'Z';

  return path;
}

/**
 * Generate SVG path for a triangle
 */
export function trianglePath(size: number, direction: 'up' | 'down'): string {
  const multiplier = direction === 'up' ? -1 : 1;
  return `M 0,${multiplier * size} L ${size},-${multiplier * size} L -${size},-${multiplier * size} Z`;
}

/**
 * Calculate distance between two points
 */
export function distance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

/**
 * Convert polar coordinates to cartesian
 */
export function polarToCartesian(
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
): { x: number; y: number } {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}
